import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Button,
  Input,
  PageContainer,
  Select,
  SurfaceCard,
  Table,
} from '@react-apps/design-system';
import type { ColumnDef } from '@react-apps/design-system';
import type { OfferOption } from '@/shared/offer-search/offers-api.datasource';
import { ErrorState, getApiErrorMessage } from '../shared/display';
import { TablePagination } from '../shared/TablePagination';
import { useOffers } from '../hooks/use-offers';
import { useProviderAccounts } from '../hooks/use-provider-accounts';
import { RefreshButton } from '@/shared/RefreshButton';
import {
  readPageParam,
  writePageParam,
} from '@/shared/hooks/use-page-query-param';
import {
  readFiltersParams,
  writeFiltersParams,
} from '@/shared/hooks/use-url-filters';

const PER_PAGE = 10;

interface OfferFilters {
  name: string;
  externalId: string;
  providerAccountId: string;
}

const EMPTY_OFFER_FILTERS: OfferFilters = {
  name: '',
  externalId: '',
  providerAccountId: '',
};

const columns: ColumnDef<OfferOption>[] = [
  {
    key: 'name',
    header: 'Oferta',
    render: (row) => (
      <span className="text-(--color-text-primary) text-xs">{row.name}</span>
    ),
  },
  {
    key: 'product',
    header: 'Produto',
    render: (row) => (
      <span className="text-(--color-text-secondary)">
        {row.productName ?? '---'}
      </span>
    ),
  },
  {
    key: 'account',
    header: 'Conta do gateway',
    render: (row) => (
      <span className="text-(--color-text-secondary)">
        {row.providerAccountName ?? row.providerAccountId}
      </span>
    ),
  },
  {
    key: 'externalId',
    header: 'ID no gateway',
    render: (row) => (
      <span className="text-(--color-text-secondary) font-mono text-xs">
        {row.externalId}
      </span>
    ),
  },
];

/**
 * Ofertas que o Sales registrou. Não há cadastro aqui: a oferta nasce da
 * ingestão do webhook, e o cadastro manual fica só na campanha de garantia.
 */
export function OffersPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Página e filtros na URL, como em Contratos: sobrevivem ao "Voltar" do
  // detalhe. As duas mudanças vão num único setSearchParams.
  const page = readPageParam(searchParams);
  const filters = readFiltersParams(searchParams, EMPTY_OFFER_FILTERS);
  const [draft, setDraft] = useState(filters);

  const setPage = (next: number) =>
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        writePageParam(params, next);
        return params;
      },
      { replace: true },
    );
  const applyFilters = (next: OfferFilters) => {
    setDraft(next);
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        writeFiltersParams(params, next, EMPTY_OFFER_FILTERS);
        writePageParam(params, 1);
        return params;
      },
      { replace: true },
    );
  };

  const { data, isLoading, isError, error, refetch, isFetching } = useOffers(
    page,
    PER_PAGE,
    {
      name: filters.name.trim() || undefined,
      externalId: filters.externalId.trim() || undefined,
      providerAccountId: filters.providerAccountId || undefined,
    },
  );
  // Falha aqui não derruba a tela: o select fica só com "Todas".
  const accounts = useProviderAccounts();

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PER_PAGE)) : 1;
  const hasActiveFilter = Object.values(filters).some(Boolean);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    applyFilters(draft);
  };

  return (
    <PageContainer maxWidth="6xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-(--color-text-primary) text-2xl font-semibold">
            Ofertas
          </h1>
          <p className="text-(--color-text-secondary) mt-1 text-sm">
            Ofertas que o Sales registrou ao receber vendas, por nome.
          </p>
        </div>
        <RefreshButton
          onRefresh={() => void refetch()}
          isRefreshing={isFetching}
        />
      </div>

      <SurfaceCard className="space-y-5">
        <form
          onSubmit={handleSubmit}
          className="grid grid-cols-1 items-end gap-4 sm:grid-cols-4"
        >
          <Input
            label="Nome"
            placeholder="Parte do nome da oferta..."
            maxLength={100}
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          />
          <Input
            label="ID da oferta ou do produto"
            placeholder="ID exato no gateway..."
            maxLength={100}
            value={draft.externalId}
            onChange={(e) => setDraft({ ...draft, externalId: e.target.value })}
          />
          <Select
            label="Conta do gateway"
            id="offers-filter-account"
            disabled={accounts.isLoading}
            options={[
              { value: '', label: 'Todas as contas' },
              ...(accounts.data ?? []).map((a) => ({
                value: a.id,
                label: a.displayName,
              })),
            ]}
            value={draft.providerAccountId}
            onChange={(e) =>
              setDraft({ ...draft, providerAccountId: e.target.value })
            }
          />
          <Button type="submit">Filtrar</Button>
        </form>

        {hasActiveFilter && (
          <div className="flex justify-end text-xs">
            <button
              type="button"
              onClick={() => applyFilters(EMPTY_OFFER_FILTERS)}
              className="text-(--color-text-secondary) hover:text-(--color-text-primary) underline"
            >
              Limpar filtros
            </button>
          </div>
        )}

        {isError ? (
          <ErrorState
            message={getApiErrorMessage(
              error,
              'Não foi possível carregar as ofertas.',
            )}
            onRetry={() => void refetch()}
          />
        ) : (
          <Table
            columns={columns}
            data={data?.items ?? []}
            keyExtractor={(row) => row.id}
            loading={isLoading}
            emptyMessage={
              hasActiveFilter
                ? 'Nenhuma oferta encontrada. Ajuste os filtros ou use Limpar filtros.'
                : 'Nenhuma oferta ainda. Elas aparecem aqui quando o Sales recebe a primeira venda de cada uma.'
            }
            striped
            onRowClick={(row) => void navigate(`/offers/${row.id}`)}
          />
        )}

        <div className="flex items-center justify-between">
          <p className="text-(--color-text-secondary) text-xs">
            {data
              ? `${data.total} ${data.total === 1 ? 'oferta encontrada' : 'ofertas encontradas'}`
              : null}
          </p>
          {totalPages > 1 && (
            <TablePagination
              page={page}
              totalPages={totalPages}
              disabled={isLoading}
              onPageChange={setPage}
            />
          )}
        </div>
      </SurfaceCard>
    </PageContainer>
  );
}
